
// vue
import { ref, defineComponent } from 'vue';
import { useRouter } from 'vue-router';
// firebase
import 'firebase/firestore';
import { db, serverTimestamp } from '@/main';
// atoms
import CustomButton from '@/components/atoms/CustomButton.vue';
import Heading from '@/components/atoms/Heading.vue';
import Loader from '@/components/atoms/Loader.vue';
import Modal from '@/components/atoms/Modal.vue';
import SelectBox from '@/components/atoms/SelectBox.vue';
import TextField from '@/components/atoms/TextField.vue';
// molecules
import ModalContent from '@/components/molecules/ModalContent.vue';
// utlis
import { validate, sha256 } from '@/utils';

export default defineComponent({
  name: 'CreateRoomWindow',
  components: {
    CustomButton,
    Heading,
    Loader,
    Modal,
    SelectBox,
    TextField,
    ModalContent,
  },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['close'],
  setup() {
    const router = useRouter();
    const studyGroupTitle = ref('');
    const adminRoomPass = ref('');
    const studentNum = ref(1);
    const expirationDate = ref('1');
    const expirationDateOptions = ref([
      { val: 1, label: '1日' },
      { val: 2, label: '2日' },
      { val: 3, label: '3日' },
      { val: 4, label: '4日' },
      { val: 5, label: '5日' },
      { val: 6, label: '6日' },
      { val: 7, label: '7日' },
      { val: 8, label: '8日' },
      { val: 9, label: '9日' },
      { val: 10, label: '10日' },
    ]);

    const titleErrMsg = ref('');
    const roomPassErrMsg = ref('');
    const studentNumErrMsg = ref('');
    const maxStudentNum = ref(500);
    const maxInputLen = ref(100);
    const validateVal = (key: string, val: any) => {
      let validateOption;
      let result;
      switch (key) {
        case 'studyGroupTitle':
          validateOption = {
            type: 'string' as 'string',
            maxLen: maxInputLen.value,
          };
          result = validate(val, validateOption);
          // バリデーションエラー
          titleErrMsg.value = result.errorMsg;
          break;
        case 'adminRoomPass':
          validateOption = { type: 'password' as 'password' };
          result = validate(val, validateOption);
          // バリデーションエラー
          roomPassErrMsg.value = result.errorMsg;
          break;
        case 'studentNum':
          validateOption = {
            type: 'number' as 'number',
            denyNegativeNum: true,
            denyZero: true,
            maxVal: maxStudentNum.value,
          };
          result = validate(val, validateOption);
          // バリデーションエラー
          studentNumErrMsg.value = result.errorMsg;
          break;
        default:
          console.error(`予期せぬkey: ${key}が渡されました。`);
          throw new Error(`予期せぬkey: ${key}が渡されました。`);
      }

      // プログラム上のエラー
      // エラーコードは/src/lib/util.tsで定義されている
      if (result.resultCode === 1) {
        alert(
          'リクエストに失敗しました。お手数ですがページをリロードして再度お試しください。'
        );
      }
    };

    const isLoading = ref(false);
    const isComplete = () => {
      // 各種エラー未解決
      if (titleErrMsg.value.length > 0) return false;
      if (roomPassErrMsg.value.length > 0) return false;
      if (studentNumErrMsg.value.length > 0) return false;
      // 各種項目未入力
      if (studyGroupTitle.value.length === 0) return false;
      if (adminRoomPass.value.length === 0) return false;
      // 数値バリデーション
      if (isNaN(Number(studentNum.value))) return false;
      if (Number(studentNum.value) < 0) return false;

      return true;
    };

    const createRoom = async () => {
      if (isComplete()) {
        isLoading.value = true;
        const hashPassword = await sha256(adminRoomPass.value);
        // ルーム作成処理
        db.collection('Room')
          .add({
            expirationDate: Number(expirationDate.value),
            roomTitle: studyGroupTitle.value,
            roomPassword: hashPassword,
            studentNum: Number(studentNum.value),
            questionIdList: [],
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          })
          .then((docRef) => {
            const id = docRef.id;
            isLoading.value = false;
            // ハッシュ化したルームパスワードをキャッシュに保存
            sessionStorage.setItem(`p${id}`, hashPassword);
            // ページ遷移後に受講者用ルームURLを表示する用フラグをセット
            sessionStorage.setItem(`isLaunch${id}`, 'true');
            router.push(`/${id}/admin`);
          })
          .catch((e) => {
            console.error(e);
            isLoading.value = false;
          });
      } else {
        alert('入力条件を満たしていない項目があります。');
      }
    };

    return {
      studyGroupTitle,
      adminRoomPass,
      studentNum,
      maxStudentNum,
      maxInputLen,
      expirationDate,
      expirationDateOptions,
      isLoading,
      titleErrMsg,
      roomPassErrMsg,
      studentNumErrMsg,
      validateVal,
      isComplete,
      createRoom,
    };
  },
});
